import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, Pencil, Trash2, Car, Wrench, CalendarDays, Loader2, Bell, Receipt, FileDown, ListPlus, GripVertical, ScanLine, Camera, Upload, Sparkles, AlertTriangle, Disc3, Send, Copy, ArrowRightLeft, Image as ImageIcon, History, CalendarClock, ClipboardList, CheckCircle2, XCircle, QrCode, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORDER_STATUS = {
  new:           { label: 'Neu',               cls: 'bg-slate-100 text-slate-700' },
  scheduled:     { label: 'Geplant',           cls: 'bg-blue-100 text-blue-700' },
  in_progress:   { label: 'In Arbeit',         cls: 'bg-amber-100 text-amber-700' },
  waiting_parts: { label: 'Wartet auf Teile',  cls: 'bg-orange-100 text-orange-700' },
  done:          { label: 'Fertig',            cls: 'bg-emerald-100 text-emerald-700' },
  picked_up:     { label: 'Abgeholt',          cls: 'bg-gray-200 text-gray-600' },
};
const STATUS_KEYS = ['new', 'scheduled', 'in_progress', 'waiting_parts', 'done', 'picked_up'];
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

const StatusBadge = ({ status }) => {
  const s = ORDER_STATUS[status] || ORDER_STATUS.new;
  return <Badge className={`${s.cls} border-0 font-medium`}>{s.label}</Badge>;
};

const EMPTY_ORDER = { vehicle_id: '', plate: '', service_description: '', status: 'new', bay_id: '', scheduled_date: '', scheduled_start: '', scheduled_end: '', assigned_to: '', positions: [], notes: '' };
const EMPTY_VEHICLE = { plate: '', make: '', model: '', year: '', vin: '', owner_name: '', owner_phone: '', next_service_date: '', reminder_opt_in: false, notes: '' };
const EMPTY_TIRE = { vehicle_id: '', customer_id: '', plate: '', owner_name: '', owner_phone: '', tire_type: 'Winter', quantity: 4, dimension: '', brand: '', rim: '', tread_depth: '', location: '', season_reminder_date: '', notes: '' };
const EMPTY_APPR = { order_id: '', vehicle_id: '', plate: '', customer_id: '', owner_name: '', owner_phone: '', title: '', description: '', items: [], photos: [] };

const WorkshopPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('orders');
  const [vehicles, setVehicles] = useState([]);
  const [bays, setBays] = useState([]);
  const [orders, setOrders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [newTpl, setNewTpl] = useState({ name: '', unit_price: '', quantity: '1', tax_rate: '8.1', category: 'Service' });
  const [loading, setLoading] = useState(true);

  // dialogs
  const [orderDlg, setOrderDlg] = useState(false);
  const [orderForm, setOrderForm] = useState(EMPTY_ORDER);
  const [editOrderId, setEditOrderId] = useState(null);
  const [editOrder, setEditOrder] = useState(null);
  const [dragTplId, setDragTplId] = useState(null);
  const [editTplId, setEditTplId] = useState(null);
  const [invoiceLang, setInvoiceLang] = useState('');
  const [vehDlg, setVehDlg] = useState(false);
  const [vehForm, setVehForm] = useState(EMPTY_VEHICLE);
  const [editVehId, setEditVehId] = useState(null);
  // Ausweis-Scan
  const [scanDlg, setScanDlg] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanForm, setScanForm] = useState(null);
  const [scanDrag, setScanDrag] = useState(false);
  // Reifenhotel
  const [tires, setTires] = useState([]);
  const [tireDlg, setTireDlg] = useState(false);
  const [tireForm, setTireForm] = useState(EMPTY_TIRE);
  const [editTireId, setEditTireId] = useState(null);
  // Foto-Freigabe
  const [approvals, setApprovals] = useState([]);
  const [apprDlg, setApprDlg] = useState(false);
  const [apprForm, setApprForm] = useState(EMPTY_APPR);
  const [apprSaving, setApprSaving] = useState(false);
  const [apprUploading, setApprUploading] = useState(false);
  const [dueReminders, setDueReminders] = useState([]);
  // Fahrzeug-Historie
  const [histDlg, setHistDlg] = useState(false);
  const [history, setHistory] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [histVehId, setHistVehId] = useState(null);
  // QR-Fahrzeugkarte
  const [qrDlg, setQrDlg] = useState(false);
  const [qrVeh, setQrVeh] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  // Online-Buchung / Öffnungszeiten
  const [booking, setBooking] = useState(null);
  // Tagesplan Drag-and-drop + QR-Sammeldruck
  const [dragOrderId, setDragOrderId] = useState(null);
  const [qrSelection, setQrSelection] = useState([]);
  const [qrBulkLoading, setQrBulkLoading] = useState(false);

  // planning
  const [planDate, setPlanDate] = useState(todayStr());
  const [planning, setPlanning] = useState(null);
  const [newBay, setNewBay] = useState('');
  const [leadDays, setLeadDays] = useState(7);

  const loadBase = useCallback(async () => {
    setLoading(true);
    try {
      const [v, b, o, s, t] = await Promise.all([
        axios.get(`${API}/workshop/vehicles`),
        axios.get(`${API}/workshop/bays`),
        axios.get(`${API}/workshop/orders`),
        axios.get(`${API}/workshop/settings`),
        axios.get(`${API}/workshop/service-templates`),
      ]);
      setVehicles(v.data || []);
      setBays(b.data || []);
      setOrders(o.data || []);
      setLeadDays(s.data?.reminder_lead_days ?? 7);
      setBooking({
        booking_enabled: s.data?.booking_enabled ?? true,
        tire_change_minutes: s.data?.tire_change_minutes ?? 30,
        min_advance_hours: s.data?.min_advance_hours ?? 2,
        max_advance_days: s.data?.max_advance_days ?? 60,
        opening_hours: s.data?.opening_hours || {},
        booking_url: s.data?.booking_url || '',
        notifications_enabled: s.data?.notifications_enabled ?? true,
        reminder_lead_hours: s.data?.reminder_lead_hours ?? 24,
        reminder_reply_enabled: s.data?.reminder_reply_enabled ?? false,
      });
      setTemplates(t.data || []);
    } catch {
      toast.error('Werkstattdaten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadBase(); }, [loadBase]);

  // QR-Deeplink: /werkstatt?vehicle=<id> öffnet direkt die Historie
  useEffect(() => {
    if (loading) return;
    const vid = new URLSearchParams(window.location.search).get('vehicle');
    if (vid && !histDlg) { setTab('vehicles'); openHistory(vid); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const loadPlanning = useCallback(async (d) => {
    try {
      const r = await axios.get(`${API}/workshop/planning`, { params: { date: d } });
      setPlanning(r.data);
    } catch {
      toast.error('Tagesplanung konnte nicht geladen werden');
    }
  }, []);
  useEffect(() => { if (tab === 'planning') loadPlanning(planDate); }, [tab, planDate, loadPlanning]);

  const loadTires = useCallback(async () => {
    try {
      const [r, d] = await Promise.all([
        axios.get(`${API}/workshop/tire-storage`),
        axios.get(`${API}/workshop/tire-storage/due-reminders`),
      ]);
      setTires(r.data || []);
      setDueReminders(d.data || []);
    } catch { toast.error('Reifenhotel konnte nicht geladen werden'); }
  }, []);
  const loadApprovals = useCallback(async () => {
    try { const r = await axios.get(`${API}/workshop/approvals`); setApprovals(r.data || []); }
    catch { toast.error('Freigaben konnten nicht geladen werden'); }
  }, []);
  useEffect(() => { if (tab === 'reifenhotel') loadTires(); if (tab === 'freigabe') loadApprovals(); }, [tab, loadTires, loadApprovals]);

  const openHistory = useCallback(async (vehicleId) => {
    setHistVehId(vehicleId); setHistDlg(true); setHistory(null); setHistLoading(true);
    try { const r = await axios.get(`${API}/workshop/vehicles/${vehicleId}/history`); setHistory(r.data); }
    catch { toast.error('Fahrzeug-Historie konnte nicht geladen werden'); }
    finally { setHistLoading(false); }
  }, []);

  const downloadHistoryPdf = async (vehicleId) => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/workshop/vehicles/${vehicleId}/history.pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Fahrzeug-Historie_${vehicleId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { toast.error('PDF konnte nicht erstellt werden'); }
  };

  const openQr = async (v) => {
    setQrVeh(v); setQrDlg(true); setQrUrl(null); setQrLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/workshop/vehicles/${v.id}/qr.png`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}, responseType: 'blob',
      });
      setQrUrl(window.URL.createObjectURL(r.data));
    } catch { toast.error('QR-Code konnte nicht erstellt werden'); }
    finally { setQrLoading(false); }
  };

  const printQr = () => {
    if (!qrUrl) return;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Bitte Pop-ups erlauben, um zu drucken.'); return; }
    const veh = qrVeh || {};
    w.document.write(`<html><head><title>QR ${veh.plate || ''}</title></head><body style="font-family:sans-serif;text-align:center;padding:40px">
      <h2>${veh.plate || 'Fahrzeug'}</h2>
      <div style="color:#555">${[veh.make, veh.model].filter(Boolean).join(' ')}</div>
      <img src="${qrUrl}" style="width:260px;height:260px;margin-top:16px" />
      <p style="color:#888;font-size:13px">Scannen → Fahrzeug-Historie &amp; Ausweis-Scan</p>
      </body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 300);
  };

  // Tagesplan: Auftrag einer Hebebühne zuweisen (Drag-and-drop)
  const assignBay = async (orderId, bayId) => {
    try {
      await axios.put(`${API}/workshop/orders/${orderId}/bay`, { bay_id: bayId });
      loadPlanning(planDate);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Zuweisung fehlgeschlagen');
    }
  };

  // Überbuchung je Bühne: zwei Aufträge mit überlappendem Zeitfenster am selben Tag.
  const bayOverbooked = (bayOrders) => {
    const mins = (t) => { if (!t) return null; const [h, m] = String(t).split(':').map(Number); return h * 60 + m; };
    const iv = (bayOrders || [])
      .map((o) => [mins(o.scheduled_start), mins(o.scheduled_end)])
      .filter(([a, b]) => a != null && b != null)
      .sort((x, y) => x[0] - y[0]);
    let maxEnd = -1;
    for (const [a, b] of iv) { if (a < maxEnd) return true; maxEnd = Math.max(maxEnd, b); }
    return false;
  };


  const renderPlanCard = (o) => (
    <div
      key={o.id}
      data-testid={`workshop-plan-card-${o.id}`}
      draggable
      onDragStart={(e) => { setDragOrderId(o.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', o.id); }}
      onDragEnd={() => setDragOrderId(null)}
      onClick={() => openEditOrder(o)}
      className={`w-full text-left rounded-lg border p-2 cursor-grab active:cursor-grabbing hover:border-orange-300 transition-colors ${o.source === 'online_booking' ? 'border-l-4 border-l-orange-400 border-gray-200' : 'border-gray-200'} ${dragOrderId === o.id ? 'opacity-50' : ''}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold flex items-center gap-1"><GripVertical className="h-3 w-3 text-gray-300" />{o.scheduled_start || '—'}{o.scheduled_end ? `–${o.scheduled_end}` : ''}</span>
        <StatusBadge status={o.status} />
      </div>
      <div className="text-sm font-medium mt-0.5">{o.vehicle_label}</div>
      <div className="text-xs text-gray-500 truncate">{o.service_description}</div>
      {o.source === 'online_booking' && <Badge className="mt-1 bg-orange-100 text-orange-700 border-0 text-[10px]">Online gebucht</Badge>}
      {o.customer_response === 'confirm' && <Badge data-testid={`workshop-plan-confirmed-${o.id}`} className="mt-1 ml-1 bg-green-100 text-green-700 border-0 text-[10px]">✓ Kunde bestätigt</Badge>}
      {o.customer_response === 'reschedule' && <Badge data-testid={`workshop-plan-reschedule-${o.id}`} className="mt-1 ml-1 bg-amber-100 text-amber-700 border-0 text-[10px]">↻ Verschiebung angefragt</Badge>}
    </div>
  );

  // QR-Sammeldruck: mehrere Fahrzeugkarten auf einmal zum Aufkleben drucken
  const toggleQrSelect = (id) => setQrSelection((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleQrSelectAll = () => setQrSelection((s) => (s.length === vehicles.length ? [] : vehicles.map((v) => v.id)));
  const blobToDataUrl = (blob) => new Promise((res) => { const r = new FileReader(); r.onloadend = () => res(r.result); r.readAsDataURL(blob); });
  const esc = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const printQrBatch = async () => {
    if (qrSelection.length === 0) { toast.error('Bitte mindestens ein Fahrzeug auswählen'); return; }
    // Fenster SYNCHRON öffnen (vor await), sonst blockiert Safari/iPad das Popup.
    const w = window.open('', '_blank');
    if (!w) { toast.error('Bitte Pop-ups erlauben, um zu drucken.'); return; }
    w.document.write('<html><head><title>QR-Karten</title></head><body style="font-family:sans-serif;padding:40px;color:#666">QR-Karten werden geladen…</body></html>');
    setQrBulkLoading(true);
    try {
      const token = localStorage.getItem('token');
      const results = await Promise.all(qrSelection.map(async (id) => {
        const v = vehicles.find((x) => x.id === id) || {};
        const r = await axios.get(`${API}/workshop/vehicles/${id}/qr.png`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}, responseType: 'blob',
        });
        const dataUrl = await blobToDataUrl(r.data);
        return { plate: esc(v.plate || 'Fahrzeug'), sub: esc([v.make, v.model].filter(Boolean).join(' ')), img: dataUrl };
      }));
      const body = results.map((c) => `<div class="card"><div class="plate">${c.plate}</div><div class="sub">${c.sub}</div><img src="${c.img}" /><div class="hint">Scannen &rarr; Fahrzeug-Historie</div></div>`).join('');
      w.document.open();
      w.document.write(`<html><head><title>QR-Karten (${results.length})</title><style>
        @page { margin: 10mm; }
        body { font-family: sans-serif; margin: 0; }
        .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8mm; }
        .card { border: 1px dashed #bbb; border-radius: 8px; padding: 6mm; text-align: center; page-break-inside: avoid; }
        .plate { font-size: 18px; font-weight: 700; }
        .sub { color: #666; font-size: 13px; margin-bottom: 6px; }
        img { width: 150px; height: 150px; }
        .hint { color: #999; font-size: 11px; margin-top: 4px; }
      </style></head><body><div class="grid">${body}</div></body></html>`);
      w.document.close();
      setTimeout(() => { w.focus(); w.print(); }, 400);
    } catch {
      try { w.close(); } catch (e) { /* noop */ }
      toast.error('QR-Sammeldruck fehlgeschlagen');
    } finally {
      setQrBulkLoading(false);
    }
  };

  // ---- Orders ----
  const openNewOrder = () => { setOrderForm({ ...EMPTY_ORDER, positions: [], scheduled_date: todayStr() }); setEditOrderId(null); setEditOrder(null); setInvoiceLang(''); setOrderDlg(true); };
  const openEditOrder = (o) => {
    setOrderForm({ vehicle_id: o.vehicle_id || '', plate: o.plate || '', service_description: o.service_description || '', status: o.status || 'new', bay_id: o.bay_id || '', scheduled_date: o.scheduled_date || '', scheduled_start: o.scheduled_start || '', scheduled_end: o.scheduled_end || '', assigned_to: o.assigned_to || '', positions: Array.isArray(o.positions) ? o.positions : [], notes: o.notes || '' });
    setEditOrderId(o.id); setEditOrder(o); setInvoiceLang(''); setOrderDlg(true);
  };
  const buildOrderBody = () => {
    const positions = (orderForm.positions || [])
      .filter((p) => (p.description || '').trim())
      .map((p) => ({ description: p.description, quantity: Number(p.quantity) || 1, unit_price: Number(p.unit_price) || 0, ...(p.tax_rate != null && p.tax_rate !== '' ? { tax_rate: Number(p.tax_rate) } : {}), ...(p.discount_type && p.discount_type !== 'none' && Number(p.discount_value) > 0 ? { discount_type: p.discount_type, discount_value: Number(p.discount_value), ...((p.discount_reason || '').trim() ? { discount_reason: p.discount_reason.trim() } : {}) } : {}) }));
    return { ...orderForm, positions, vehicle_id: orderForm.vehicle_id || null, bay_id: orderForm.bay_id || null };
  };
  const saveOrder = async () => {
    if (!orderForm.service_description.trim()) { toast.error('Leistungsbeschreibung erforderlich'); return; }
    const body = buildOrderBody();
    try {
      if (editOrderId) await axios.put(`${API}/workshop/orders/${editOrderId}`, body);
      else await axios.post(`${API}/workshop/orders`, body);
      toast.success('Auftrag gespeichert');
      setOrderDlg(false);
      loadBase();
      if (tab === 'planning') loadPlanning(planDate);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
  };
  const deleteOrder = async (id) => {
    if (!window.confirm('Diesen Auftrag wirklich löschen?')) return;
    try { await axios.delete(`${API}/workshop/orders/${id}`); toast.success('Auftrag gelöscht'); loadBase(); if (tab === 'planning') loadPlanning(planDate); }
    catch { toast.error('Löschen fehlgeschlagen'); }
  };
  const downloadInvoicePdf = async (invoiceId, invoiceNumber, lang) => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params: lang ? { lang } : {},
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const _sfx = lang && lang !== 'de' ? `_${lang}` : '';
      a.download = `Rechnung_${invoiceNumber || invoiceId}${_sfx}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('PDF konnte nicht geladen werden');
    }
  };
  const createInvoice = async (o) => {
    if (!window.confirm('Entwurf-Rechnung aus diesem Auftrag erstellen? (kein Zahlungseinzug)')) return;
    try {
      const r = await axios.post(`${API}/workshop/orders/${o.id}/invoice`);
      const { invoice_id, invoice_number, total } = r.data;
      toast.success(`Entwurf-Rechnung ${invoice_number} erstellt (CHF ${total})`, {
        action: { label: 'Rechnung öffnen', onClick: () => navigate(`/invoices?search=${encodeURIComponent(invoice_number)}`) },
      });
      setEditOrder((eo) => (eo && eo.id === o.id ? { ...eo, invoice_id, invoice_number } : eo));
      downloadInvoicePdf(invoice_id, invoice_number, invoiceLang || undefined);
      loadBase();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rechnung fehlgeschlagen'); }
  };
  // Dialog: aktuelle (ggf. ungespeicherte) Positionen zuerst sichern, dann Entwurf-Rechnung erstellen
  const createInvoiceFromDialog = async () => {
    if (!editOrderId) return;
    if (!window.confirm('Entwurf-Rechnung aus diesem Auftrag erstellen? (kein Zahlungseinzug)')) return;
    try {
      await axios.put(`${API}/workshop/orders/${editOrderId}`, buildOrderBody());
    } catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); return; }
    try {
      const r = await axios.post(`${API}/workshop/orders/${editOrderId}/invoice`);
      const { invoice_id, invoice_number, total } = r.data;
      toast.success(`Entwurf-Rechnung ${invoice_number} erstellt (CHF ${total})`, {
        action: { label: 'Rechnung öffnen', onClick: () => navigate(`/invoices?search=${encodeURIComponent(invoice_number)}`) },
      });
      setEditOrder((eo) => (eo ? { ...eo, invoice_id, invoice_number } : eo));
      downloadInvoicePdf(invoice_id, invoice_number, invoiceLang || undefined);
      loadBase();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rechnung fehlgeschlagen'); }
  };
  const saveSettings = async () => {
    try {
      await axios.put(`${API}/workshop/settings`, {
        reminder_lead_days: Number(leadDays) || 0,
        ...(booking ? {
          booking_enabled: booking.booking_enabled,
          tire_change_minutes: Number(booking.tire_change_minutes) || 30,
          min_advance_hours: Number(booking.min_advance_hours) || 0,
          max_advance_days: Number(booking.max_advance_days) || 60,
          opening_hours: booking.opening_hours,
          notifications_enabled: booking.notifications_enabled,
          reminder_lead_hours: Number(booking.reminder_lead_hours) || 24,
          reminder_reply_enabled: booking.reminder_reply_enabled,
        } : {}),
      });
      toast.success('Einstellungen gespeichert');
    } catch { toast.error('Speichern fehlgeschlagen'); }
  };
  const WD = [['mon', 'Mo'], ['tue', 'Di'], ['wed', 'Mi'], ['thu', 'Do'], ['fri', 'Fr'], ['sat', 'Sa'], ['sun', 'So']];
  const setDayOpen = (day, open) => setBooking((b) => ({ ...b, opening_hours: { ...b.opening_hours, [day]: open ? [{ from: '08:00', to: '12:00' }, { from: '13:30', to: '17:30' }] : [] } }));
  const setWin = (day, idx, field, val) => setBooking((b) => {
    const wins = [...((b.opening_hours || {})[day] || [])];
    wins[idx] = { ...wins[idx], [field]: val };
    return { ...b, opening_hours: { ...b.opening_hours, [day]: wins } };
  });
  // Positions-Editor
  const addPosition = () => setOrderForm((f) => ({ ...f, positions: [...(f.positions || []), { description: '', quantity: 1, unit_price: 0, discount_type: 'none', discount_value: '', discount_reason: '' }] }));
  const updatePosition = (i, key, val) => setOrderForm((f) => { const ps = [...(f.positions || [])]; ps[i] = { ...ps[i], [key]: val }; if (key === 'discount_type' && val === 'none') ps[i].discount_value = ''; return { ...f, positions: ps }; });
  const removePosition = (i) => setOrderForm((f) => ({ ...f, positions: (f.positions || []).filter((_, idx) => idx !== i) }));
  const addTemplatePosition = (tpl) => setOrderForm((f) => ({ ...f, positions: [...(f.positions || []), { description: tpl.name, quantity: Number(tpl.quantity) || 1, unit_price: Number(tpl.unit_price) || 0, tax_rate: tpl.tax_rate != null ? Number(tpl.tax_rate) : undefined, discount_type: 'none', discount_value: '', discount_reason: '' }] }));

  // ---- Service-Vorlagen ----
  const startEditTemplate = (t) => {
    setEditTplId(t.id);
    setNewTpl({
      name: t.name || '',
      unit_price: t.unit_price == null ? '' : String(t.unit_price),
      quantity: String(t.quantity || 1),
      tax_rate: t.tax_rate == null ? '' : String(t.tax_rate),
      category: t.category || 'Weitere',
    });
  };
  const cancelEditTemplate = () => {
    setEditTplId(null);
    setNewTpl({ name: '', unit_price: '', quantity: '1', tax_rate: '8.1', category: newTpl.category || 'Service' });
  };
  const addTemplate = async () => {
    if (!newTpl.name.trim()) { toast.error('Bezeichnung erforderlich'); return; }
    const body = {
      name: newTpl.name.trim(),
      unit_price: Number(newTpl.unit_price) || 0,
      quantity: Math.max(1, parseInt(newTpl.quantity, 10) || 1),
      tax_rate: newTpl.tax_rate === '' ? null : Number(newTpl.tax_rate),
      category: (newTpl.category || '').trim() || 'Weitere',
    };
    try {
      if (editTplId) {
        await axios.put(`${API}/workshop/service-templates/${editTplId}`, body);
      } else {
        await axios.post(`${API}/workshop/service-templates`, body);
      }
      setNewTpl({ name: '', unit_price: '', quantity: '1', tax_rate: '8.1', category: newTpl.category || 'Service' });
      setEditTplId(null);
      const t = await axios.get(`${API}/workshop/service-templates`);
      setTemplates(t.data || []);
      toast.success(editTplId ? 'Vorlage aktualisiert' : 'Vorlage gespeichert');
    } catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
  };
  const deleteTemplate = async (id) => {
    try {
      await axios.delete(`${API}/workshop/service-templates/${id}`);
      setTemplates((ts) => ts.filter((t) => t.id !== id));
      if (editTplId === id) cancelEditTemplate();
      toast.success('Vorlage gelöscht');
    } catch { toast.error('Löschen fehlgeschlagen'); }
  };

  // Vorschläge beim Abschluss: passende Vorlagen zur Leistungsbeschreibung
  const suggestedTemplates = useMemo(() => {
    const isDone = ['done', 'picked_up'].includes(orderForm.status);
    const hasPositions = (orderForm.positions || []).some((p) => (p.description || '').trim());
    if (!isDone || hasPositions || templates.length === 0) return [];
    const desc = (orderForm.service_description || '').toLowerCase();
    if (!desc.trim()) return [];
    const matched = templates.filter((t) => {
      const words = (t.name || '').toLowerCase().split(/[^a-zäöüß0-9]+/).filter((w) => w.length >= 3);
      return words.some((w) => desc.includes(w));
    });
    return matched;
  }, [orderForm.status, orderForm.positions, orderForm.service_description, templates]);
  const applyAllSuggestions = () => suggestedTemplates.forEach((t) => addTemplatePosition(t));

  // Vorlagen nach Kategorie gruppieren
  const groupedTemplates = useMemo(() => {
    const groups = {};
    templates.forEach((t) => { const c = t.category || 'Weitere'; (groups[c] = groups[c] || []).push(t); });
    const order = ['Service', 'Reifen', 'Bremsen'];
    return Object.keys(groups).sort((a, b) => {
      const ia = order.indexOf(a), ib = order.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
    }).map((c) => ({ category: c, items: groups[c] }));
  }, [templates]);

  // Vorlagen-Reihenfolge per Drag & Drop (innerhalb einer Kategorie)
  const reorderTemplates = async (targetId, category) => {
    if (!dragTplId || dragTplId === targetId) { setDragTplId(null); return; }
    const catItems = templates.filter((t) => (t.category || 'Weitere') === category);
    const ids = catItems.map((t) => t.id);
    const from = ids.indexOf(dragTplId), to = ids.indexOf(targetId);
    if (from === -1 || to === -1) { setDragTplId(null); return; }
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    const map = Object.fromEntries(templates.map((t) => [t.id, t]));
    const newCat = ids.map((id) => map[id]);
    let ci = 0;
    const next = templates.map((t) => ((t.category || 'Weitere') === category ? newCat[ci++] : t));
    setTemplates(next);
    setDragTplId(null);
    try { await axios.put(`${API}/workshop/service-templates/reorder`, { ordered_ids: ids }); }
    catch { toast.error('Reihenfolge konnte nicht gespeichert werden'); loadBase(); }
  };

  // Rechnungsvorschau (Netto/MwSt/Brutto je Position + Totals) – spiegelt Backend
  const invoicePreview = useMemo(() => {
    const lines = (orderForm.positions || [])
      .filter((p) => (p.description || '').trim())
      .map((p) => {
        const qty = Number(p.quantity) || 1;
        const unit = Number(p.unit_price) || 0;
        const rate = (p.tax_rate != null && p.tax_rate !== '') ? Number(p.tax_rate) : 8.1;
        const base = unit * qty;
        const stdNet = rate > 0 ? base / (1 + rate / 100) : base;
        const dtype = p.discount_type;
        const dval = Number(p.discount_value) || 0;
        let discGross = dtype === 'percent' ? base * dval / 100 : dtype === 'amount' ? dval : 0;
        discGross = Math.min(Math.max(discGross, 0), base);
        const gross = base - discGross;
        const net = rate > 0 ? gross / (1 + rate / 100) : gross;
        const tax = gross - net;
        const disc = stdNet - net; // Netto-Rabatt (spiegelt Rechnung/PDF)
        return { description: p.description, qty, rate, gross, net, tax, disc, dtype, dval };
      });
    const totals = lines.reduce((a, l) => ({ net: a.net + l.net, tax: a.tax + l.tax, gross: a.gross + l.gross, disc: a.disc + l.disc }), { net: 0, tax: 0, gross: 0, disc: 0 });
    return { lines, totals };
  }, [orderForm.positions]);

  // ---- Vehicles ----
  const openNewVeh = () => { setVehForm(EMPTY_VEHICLE); setEditVehId(null); setVehDlg(true); };
  const openEditVeh = (v) => { setVehForm({ plate: v.plate || '', make: v.make || '', model: v.model || '', year: v.year || '', vin: v.vin || '', owner_name: v.owner_name || '', owner_phone: v.owner_phone || '', next_service_date: v.next_service_date || '', reminder_opt_in: !!v.reminder_opt_in, notes: v.notes || '' }); setEditVehId(v.id); setVehDlg(true); };
  const saveVeh = async () => {
    if (!vehForm.plate.trim()) { toast.error('Kennzeichen erforderlich'); return; }
    const body = { ...vehForm, year: vehForm.year ? parseInt(vehForm.year, 10) : null };
    try {
      if (editVehId) await axios.put(`${API}/workshop/vehicles/${editVehId}`, body);
      else await axios.post(`${API}/workshop/vehicles`, body);
      toast.success('Fahrzeug gespeichert');
      setVehDlg(false); loadBase();
    } catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
  };
  const deleteVeh = async (id) => {
    if (!window.confirm('Dieses Fahrzeug wirklich löschen?')) return;
    try { await axios.delete(`${API}/workshop/vehicles/${id}`); toast.success('Fahrzeug gelöscht'); loadBase(); }
    catch { toast.error('Löschen fehlgeschlagen'); }
  };
  const sendReminder = async (id) => {
    try { const r = await axios.post(`${API}/workshop/vehicles/${id}/send-reminder`); toast.success(r.data?.message || 'Erinnerung gesendet'); loadBase(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen'); }
  };

  // ---- Ausweis-Scan (Fahrzeugausweis -> KI-Datenextraktion) ----
  const openScan = () => { setScanResult(null); setScanForm(null); setScanning(false); setScanDrag(false); setScanDlg(true); };
  const handleScanFile = async (fileObj) => {
    if (!fileObj) return;
    if (!/^image\//.test(fileObj.type || '')) { toast.error('Bitte ein Foto oder Bild des Ausweises wählen'); return; }
    setScanning(true); setScanResult(null); setScanForm(null);
    try {
      const fd = new FormData(); fd.append('file', fileObj);
      const r = await axios.post(`${API}/workshop/scan-document`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = r.data; const ex = d.extracted || {};
      setScanResult(d);
      setScanForm({
        plate: ex.plate || '', make: ex.make || '', model: ex.model || '',
        vin: ex.vin || '', vehicle_type: ex.vehicle_type || '',
        first_registration: ex.first_registration || '', color: ex.color || '',
        year: ex.year || '',
        document_image_url: d.document_image_url || '',
        customer_mode: d.existing_vehicle?.customer_id ? 'existing' : 'new',
        customer_id: d.existing_vehicle?.customer_id || (d.customer_matches?.[0]?.id || ''),
        owner_name: ex.owner_name || '', owner_phone: ex.owner_phone || '',
        owner_first_name: ex.owner_first_name || '', owner_last_name: ex.owner_last_name || '',
        owner_address: ex.owner_address || '', owner_zip: ex.owner_zip || '', owner_city: ex.owner_city || '',
        vehicle_id: d.existing_vehicle?.id || null,
      });
    } catch (e) { toast.error(e.response?.data?.detail || 'Scan fehlgeschlagen'); }
    finally { setScanning(false); }
  };
  const onScanDrop = (e) => { e.preventDefault(); setScanDrag(false); const f = e.dataTransfer?.files?.[0]; if (f) handleScanFile(f); };
  const confirmScan = async () => {
    if (!scanForm?.plate?.trim()) { toast.error('Schildnummer erforderlich'); return; }
    if (scanForm.customer_mode === 'existing' && !scanForm.customer_id) { toast.error('Bitte einen Kunden auswählen'); return; }
    if (scanForm.customer_mode === 'new' && !scanForm.owner_last_name?.trim() && !scanForm.owner_first_name?.trim()) { toast.error('Name des Halters erforderlich'); return; }
    try {
      const body = { ...scanForm, year: scanForm.year ? parseInt(scanForm.year, 10) : null };
      if (scanForm.customer_mode === 'new') {
        body.owner_name = `${scanForm.owner_first_name || ''} ${scanForm.owner_last_name || ''}`.trim();
      }
      const r = await axios.post(`${API}/workshop/scan-document/confirm`, body);
      toast.success(r.data?.message || 'Fahrzeug gespeichert');
      setScanDlg(false); loadBase();
    } catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
  };
  const updateScan = (key, val) => setScanForm((f) => ({ ...f, [key]: val }));

  // ---- Reifenhotel ----
  const pickVehicleInto = (vid, setter) => {
    const v = vehicles.find((x) => x.id === vid);
    setter((f) => ({ ...f, vehicle_id: vid, plate: v?.plate || f.plate, customer_id: v?.customer_id || f.customer_id, owner_name: v?.owner_name || f.owner_name, owner_phone: v?.owner_phone || f.owner_phone }));
  };
  const openNewTire = () => { setTireForm({ ...EMPTY_TIRE }); setEditTireId(null); setTireDlg(true); };
  const openEditTire = (t) => { setTireForm({ ...EMPTY_TIRE, ...t }); setEditTireId(t.id); setTireDlg(true); };
  const saveTire = async () => {
    if (!tireForm.location?.trim()) { toast.error('Lagerplatz erforderlich'); return; }
    const body = { ...tireForm, quantity: Number(tireForm.quantity) || 4, vehicle_id: tireForm.vehicle_id || null, customer_id: tireForm.customer_id || null, season_reminder_date: tireForm.season_reminder_date || null };
    try {
      if (editTireId) await axios.put(`${API}/workshop/tire-storage/${editTireId}`, body);
      else await axios.post(`${API}/workshop/tire-storage`, body);
      toast.success('Reifensatz gespeichert'); setTireDlg(false); loadTires();
    } catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
  };
  const tireAction = async (id, action) => {
    try { await axios.post(`${API}/workshop/tire-storage/${id}/${action}`); toast.success('Aktualisiert'); loadTires(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };
  const tireReminder = async (id) => {
    try { const r = await axios.post(`${API}/workshop/tire-storage/${id}/send-reminder`); toast.success(r.data?.message || 'Erinnerung gesendet'); loadTires(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen'); }
  };
  const deleteTire = async (id) => {
    if (!window.confirm('Reifensatz wirklich löschen?')) return;
    try { await axios.delete(`${API}/workshop/tire-storage/${id}`); toast.success('Reifensatz gelöscht'); loadTires(); } catch { toast.error('Löschen fehlgeschlagen'); }
  };

  // ---- Foto-Freigabe ----
  const openNewAppr = () => { setApprForm({ ...EMPTY_APPR }); setApprDlg(true); };
  const apprAddItem = () => setApprForm((f) => ({ ...f, items: [...f.items, { label: '', price: '' }] }));
  const apprUpdItem = (i, k, v) => setApprForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) }));
  const apprRemItem = (i) => setApprForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const apprUploadPhoto = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type || '')) { toast.error('Bitte ein Bild wählen'); return; }
    setApprUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/workshop/approvals/upload-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setApprForm((f) => ({ ...f, photos: [...f.photos, r.data.url] }));
    } catch { toast.error('Foto-Upload fehlgeschlagen'); }
    finally { setApprUploading(false); }
  };
  const apprRemPhoto = (i) => setApprForm((f) => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }));
  const saveAppr = async () => {
    if (!apprForm.title.trim()) { toast.error('Titel erforderlich'); return; }
    setApprSaving(true);
    try {
      const body = { ...apprForm, vehicle_id: apprForm.vehicle_id || null, customer_id: apprForm.customer_id || null, items: apprForm.items.filter((i) => i.label.trim()).map((i) => ({ label: i.label.trim(), price: Number(i.price) || 0 })) };
      await axios.post(`${API}/workshop/approvals`, body);
      toast.success('Freigabe-Anfrage erstellt'); setApprDlg(false); loadApprovals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setApprSaving(false); }
  };
  const sendAppr = async (id) => {
    try { const r = await axios.post(`${API}/workshop/approvals/${id}/send`); toast.success(r.data?.message || 'Link gesendet'); loadApprovals(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen'); }
  };
  const copyApprLink = async (a) => {
    const link = a.public_link || `${window.location.origin}/werkstatt-freigabe/${a.token}`;
    try { await navigator.clipboard.writeText(link); toast.success('Link kopiert'); } catch { toast.message(link); }
  };
  const deleteAppr = async (id) => {
    if (!window.confirm('Freigabe wirklich löschen?')) return;
    try { await axios.delete(`${API}/workshop/approvals/${id}`); toast.success('Freigabe gelöscht'); loadApprovals(); } catch { toast.error('Löschen fehlgeschlagen'); }
  };
  const APPR_STATUS = { pending: { label: 'Offen', cls: 'bg-amber-100 text-amber-700' }, approved: { label: 'Freigegeben', cls: 'bg-green-100 text-green-700' }, rejected: { label: 'Abgelehnt', cls: 'bg-red-100 text-red-700' } };
  const TIRE_FULL = `${process.env.REACT_APP_BACKEND_URL}`;

  // ---- Bays ----
  const addBay = async () => {
    if (!newBay.trim()) return;
    try { await axios.post(`${API}/workshop/bays`, { name: newBay.trim() }); setNewBay(''); loadBase(); loadPlanning(planDate); toast.success('Hebebühne hinzugefügt'); }
    catch { toast.error('Fehlgeschlagen'); }
  };
  const deleteBay = async (id) => {
    if (!window.confirm('Diese Hebebühne löschen? Zugeteilte Aufträge werden gelöst.')) return;
    try { await axios.delete(`${API}/workshop/bays/${id}`); loadBase(); loadPlanning(planDate); toast.success('Hebebühne gelöscht'); }
    catch { toast.error('Löschen fehlgeschlagen'); }
  };

  return (
    <div className="space-y-6" data-testid="workshop-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center"><Wrench className="text-orange-600" size={22} /></div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Werkstatt</h1>
          <p className="text-sm text-gray-500">Fahrzeuge, Aufträge und Hebebühnen-Tagesplanung.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="orders" data-testid="workshop-tab-orders"><Wrench className="h-4 w-4 mr-2" />Aufträge</TabsTrigger>
          <TabsTrigger value="vehicles" data-testid="workshop-tab-vehicles"><Car className="h-4 w-4 mr-2" />Fahrzeuge</TabsTrigger>
          <TabsTrigger value="reifenhotel" data-testid="workshop-tab-reifenhotel"><Disc3 className="h-4 w-4 mr-2" />Reifenhotel</TabsTrigger>
          <TabsTrigger value="freigabe" data-testid="workshop-tab-freigabe"><ImageIcon className="h-4 w-4 mr-2" />Foto-Freigabe</TabsTrigger>
          <TabsTrigger value="planning" data-testid="workshop-tab-planning"><CalendarDays className="h-4 w-4 mr-2" />Tagesplanung</TabsTrigger>
        </TabsList>

        {/* ORDERS */}
        <TabsContent value="orders" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button onClick={openNewOrder} data-testid="workshop-new-order-btn" className="bg-orange-600 hover:bg-orange-700"><Plus className="h-4 w-4 mr-2" />Neuer Auftrag</Button>
          </div>
          <Card><CardContent className="p-0">
            {loading ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div> : orders.length === 0 ? (
              <div className="py-12 text-center text-gray-400">Noch keine Aufträge.</div>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Fahrzeug</TableHead><TableHead>Leistung</TableHead><TableHead>Status</TableHead>
                  <TableHead>Hebebühne</TableHead><TableHead>Termin</TableHead><TableHead className="text-right">Aktion</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {orders.map((o) => (
                    <TableRow key={o.id} data-testid={`workshop-order-row-${o.id}`}>
                      <TableCell className="font-medium">{o.vehicle_label}</TableCell>
                      <TableCell className="max-w-[260px] truncate">{o.service_description}</TableCell>
                      <TableCell><StatusBadge status={o.status} /></TableCell>
                      <TableCell>{o.bay_name || '—'}</TableCell>
                      <TableCell className="text-sm text-gray-600">{o.scheduled_date ? `${o.scheduled_date}${o.scheduled_start ? ' ' + o.scheduled_start : ''}` : '—'}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {o.invoice_id ? (
                          <Button variant="ghost" size="sm" data-testid={`workshop-order-pdf-${o.id}`} onClick={() => downloadInvoicePdf(o.invoice_id, o.invoice_number)} className="text-emerald-700 mr-1" title="Rechnungs-PDF öffnen"><FileDown className="h-4 w-4 mr-1" />RG {o.invoice_number}</Button>
                        ) : (['done', 'picked_up'].includes(o.status) ? (
                          <Button variant="ghost" size="sm" data-testid={`workshop-order-invoice-${o.id}`} onClick={() => createInvoice(o)} className="text-orange-600" title="Entwurf-Rechnung erstellen"><Receipt className="h-4 w-4" /></Button>
                        ) : null)}
                        <Button variant="ghost" size="sm" data-testid={`workshop-order-edit-${o.id}`} onClick={() => openEditOrder(o)}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" data-testid={`workshop-order-delete-${o.id}`} onClick={() => deleteOrder(o.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* VEHICLES */}
        <TabsContent value="vehicles" className="mt-4">
          <div className="flex justify-end gap-2 mb-3">
            <Button onClick={printQrBatch} disabled={qrBulkLoading || qrSelection.length === 0} data-testid="workshop-qr-batch-print-btn" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50">
              {qrBulkLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <QrCode className="h-4 w-4 mr-2" />}QR-Karten drucken{qrSelection.length ? ` (${qrSelection.length})` : ''}
            </Button>
            <Button onClick={openScan} data-testid="workshop-scan-vehicle-btn" variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50"><ScanLine className="h-4 w-4 mr-2" />Ausweis scannen</Button>
            <Button onClick={openNewVeh} data-testid="workshop-new-vehicle-btn" className="bg-orange-600 hover:bg-orange-700"><Plus className="h-4 w-4 mr-2" />Neues Fahrzeug</Button>
          </div>
          <Card><CardContent className="p-0">
            {loading ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div> : vehicles.length === 0 ? (
              <div className="py-12 text-center text-gray-400">Noch keine Fahrzeuge.</div>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="w-10">
                    <input type="checkbox" data-testid="workshop-qr-select-all" checked={vehicles.length > 0 && qrSelection.length === vehicles.length} onChange={toggleQrSelectAll} className="h-4 w-4 cursor-pointer accent-orange-600" title="Alle für QR-Druck auswählen" />
                  </TableHead>
                  <TableHead>Kennzeichen</TableHead><TableHead>Marke / Modell</TableHead><TableHead>Jahr</TableHead><TableHead>Halter</TableHead><TableHead>Nächster Service</TableHead><TableHead className="text-right">Aktion</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {vehicles.map((v) => (
                    <TableRow key={v.id} data-testid={`workshop-vehicle-row-${v.id}`}>
                      <TableCell>
                        <input type="checkbox" data-testid={`workshop-qr-select-${v.id}`} checked={qrSelection.includes(v.id)} onChange={() => toggleQrSelect(v.id)} className="h-4 w-4 cursor-pointer accent-orange-600" />
                      </TableCell>
                      <TableCell className="font-semibold">{v.plate}</TableCell>
                      <TableCell>{[v.make, v.model].filter(Boolean).join(' ') || '—'}</TableCell>
                      <TableCell>{v.year || '—'}</TableCell>
                      <TableCell>{v.owner_name || '—'}</TableCell>
                      <TableCell className="text-sm">
                        {v.next_service_date || '—'}
                        {v.reminder_opt_in ? <Badge className="ml-2 bg-teal-100 text-teal-700 border-0">Erinnerung an</Badge> : null}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" data-testid={`workshop-vehicle-history-${v.id}`} onClick={() => openHistory(v.id)} title="Fahrzeug-Historie anzeigen" className="text-orange-600"><History className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" data-testid={`workshop-vehicle-qr-${v.id}`} onClick={() => openQr(v)} title="QR-Fahrzeugkarte" className="text-slate-600"><QrCode className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" data-testid={`workshop-vehicle-reminder-${v.id}`} onClick={() => sendReminder(v.id)} title="Service-Erinnerung per WhatsApp senden" className="text-teal-600"><Bell className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" data-testid={`workshop-vehicle-edit-${v.id}`} onClick={() => openEditVeh(v)}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" data-testid={`workshop-vehicle-delete-${v.id}`} onClick={() => deleteVeh(v.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* PLANNING */}
        <TabsContent value="planning" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Datum</Label>
              <Input type="date" value={planDate} onChange={(e) => setPlanDate(e.target.value)} data-testid="workshop-planning-date" className="mt-1 w-44" />
            </div>
            <div className="flex items-end gap-2">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hebebühne hinzufügen</Label>
                <Input value={newBay} onChange={(e) => setNewBay(e.target.value)} placeholder="z. B. Bühne 2" data-testid="workshop-add-bay-input" className="mt-1 w-44" />
              </div>
              <Button onClick={addBay} variant="outline" data-testid="workshop-add-bay-btn"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="flex items-end gap-2">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Erinnerung (Tage vorher)</Label>
                <Input type="number" min="0" max="90" value={leadDays} onChange={(e) => setLeadDays(e.target.value)} data-testid="workshop-lead-days" className="mt-1 w-36" />
              </div>
              <Button onClick={saveSettings} variant="outline" data-testid="workshop-save-settings-btn">Speichern</Button>
            </div>
          </div>

          {/* Online-Terminbuchung / Öffnungszeiten */}
          <Card data-testid="workshop-booking-card">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><CalendarClock className="h-4 w-4 text-orange-600" />Online-Terminbuchung (Reifenwechsel)</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {booking && (<>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                  <label className="flex items-center gap-2 text-sm">
                    <Switch checked={!!booking.booking_enabled} onCheckedChange={(v) => setBooking((b) => ({ ...b, booking_enabled: v }))} data-testid="workshop-booking-enabled" />
                    Online-Buchung aktiv
                  </label>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500">Dauer pro Termin</span>
                    <Input type="number" min="10" max="240" value={booking.tire_change_minutes} onChange={(e) => setBooking((b) => ({ ...b, tire_change_minutes: e.target.value }))} data-testid="workshop-booking-duration" className="w-20" />
                    <span className="text-gray-400">Min.</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500">Vorlauf min.</span>
                    <Input type="number" min="0" max="168" value={booking.min_advance_hours} onChange={(e) => setBooking((b) => ({ ...b, min_advance_hours: e.target.value }))} data-testid="workshop-booking-advance" className="w-20" />
                    <span className="text-gray-400">Std.</span>
                  </div>
                  <div className="text-sm text-gray-500">Gleichzeitige Termine = <span className="font-semibold text-orange-700" data-testid="workshop-booking-capacity">{bays.length || 1} Hebebühne(n)</span></div>
                </div>
                <label className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                  <Switch checked={!!booking.notifications_enabled} onCheckedChange={(v) => setBooking((b) => ({ ...b, notifications_enabled: v }))} data-testid="workshop-notifications-enabled" className="mt-0.5" />
                  <span className="text-sm">
                    <span className="font-medium">Automatische Bestätigung &amp; Erinnerung</span>
                    <span className="block text-xs text-gray-500">Kunde erhält nach der Buchung eine Bestätigung und vor dem Termin eine Erinnerung – per WhatsApp &amp; E-Mail, sonst SMS. Diese Dienstleistung wird zentral durch die Invictax Group bereitgestellt.</span>
                  </span>
                </label>
                {booking.notifications_enabled && (
                  <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 space-y-3" data-testid="workshop-reminder-options">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium">Erinnerung</span>
                      <Input
                        type="number" min="1" max="168"
                        value={booking.reminder_lead_hours}
                        onChange={(e) => setBooking((b) => ({ ...b, reminder_lead_hours: e.target.value }))}
                        data-testid="workshop-reminder-lead-hours"
                        className="w-20"
                      />
                      <span className="text-sm text-gray-600">Stunden vor dem Termin senden</span>
                    </div>
                    <label className="flex items-start gap-3">
                      <Switch
                        checked={!!booking.reminder_reply_enabled}
                        onCheckedChange={(v) => setBooking((b) => ({ ...b, reminder_reply_enabled: v }))}
                        data-testid="workshop-reminder-reply-enabled"
                        className="mt-0.5"
                      />
                      <span className="text-sm">
                        <span className="font-medium">Antwort-Link in der Erinnerung</span>
                        <span className="block text-xs text-gray-500">Kunde kann den Termin per Link bestätigen oder eine Verschiebung anfragen.</span>
                      </span>
                    </label>
                  </div>
                )}
                <div className="rounded-lg border border-gray-200 divide-y">
                  {WD.map(([key, lbl]) => {
                    const wins = (booking.opening_hours || {})[key] || [];
                    const open = wins.length > 0;
                    return (
                      <div key={key} className="flex flex-wrap items-center gap-3 px-3 py-2" data-testid={`workshop-hours-${key}`}>
                        <div className="w-24 flex items-center gap-2">
                          <Switch checked={open} onCheckedChange={(v) => setDayOpen(key, v)} data-testid={`workshop-hours-toggle-${key}`} />
                          <span className="text-sm font-medium">{lbl}</span>
                        </div>
                        {open ? (
                          <div className="flex flex-wrap items-center gap-2 text-sm">
                            {[0, 1].map((i) => (
                              <span key={i} className="flex items-center gap-1">
                                {i === 1 && <span className="text-gray-300 mx-1">·</span>}
                                <Input type="time" value={wins[i]?.from || ''} onChange={(e) => setWin(key, i, 'from', e.target.value)} className="w-28" />
                                <span className="text-gray-400">–</span>
                                <Input type="time" value={wins[i]?.to || ''} onChange={(e) => setWin(key, i, 'to', e.target.value)} className="w-28" />
                              </span>
                            ))}
                          </div>
                        ) : <span className="text-sm text-gray-400">geschlossen</span>}
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button onClick={saveSettings} data-testid="workshop-booking-save-btn" className="bg-orange-600 hover:bg-orange-700">Buchungs-Einstellungen speichern</Button>
                  <Button variant="outline" data-testid="workshop-booking-open-btn" onClick={() => window.open(booking.booking_url || `${window.location.origin}/werkstatt-buchen?service=reifenwechsel`, '_blank')}><ExternalLink className="h-4 w-4 mr-1.5" />Buchungsseite ansehen</Button>
                  <Button variant="outline" data-testid="workshop-booking-copy-btn" onClick={() => { navigator.clipboard?.writeText(booking.booking_url || `${window.location.origin}/werkstatt-buchen?service=reifenwechsel`); toast.success('Buchungslink kopiert'); }}><Copy className="h-4 w-4 mr-1.5" />Link kopieren</Button>
                </div>
              </>)}
            </CardContent>
          </Card>

          {/* Positions-Vorlagen Verwaltung */}
          <Card data-testid="workshop-templates-card">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><ListPlus className="h-4 w-4 text-orange-600" />Positions-Vorlagen</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-gray-500">Häufige Leistungen mit Standardpreis – im Auftragsdialog per Klick als Position einfügbar. Innerhalb einer Kategorie per Ziehen (Griff) sortierbar.</p>
              <div className="space-y-2">
                {groupedTemplates.map((g) => (
                  <div key={g.category}>
                    <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">{g.category}</div>
                    <div className="flex flex-wrap gap-2">
                      {g.items.map((t) => (
                        <Badge
                          key={t.id}
                          variant="outline"
                          data-testid={`workshop-template-badge-${t.id}`}
                          draggable
                          onDragStart={(e) => { setDragTplId(t.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', t.id); }}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => reorderTemplates(t.id, g.category)}
                          className={`flex items-center gap-1.5 py-1.5 pl-2 pr-1.5 text-sm font-normal cursor-move ${dragTplId === t.id ? 'opacity-50' : ''}`}
                        >
                          <GripVertical className="h-3.5 w-3.5 text-gray-400" />
                          {(t.quantity > 1 ? `${t.quantity}× ` : '') + t.name} · CHF {Number(t.unit_price).toFixed(0)}{t.tax_rate != null ? ` · ${Number(t.tax_rate)}%` : ''}
                          <button onClick={() => startEditTemplate(t)} data-testid={`workshop-template-edit-${t.id}`} className="ml-1 rounded-full p-0.5 hover:bg-orange-50 text-orange-500"><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={() => deleteTemplate(t.id)} data-testid={`workshop-template-delete-${t.id}`} className="rounded-full p-0.5 hover:bg-red-50 text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
                {templates.length === 0 && <span className="text-xs text-gray-400">Noch keine Vorlagen.</span>}
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <div><Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Bezeichnung</Label><Input value={newTpl.name} onChange={(e) => setNewTpl((f) => ({ ...f, name: e.target.value }))} placeholder="z. B. 4× Reifen" data-testid="workshop-template-name" className="mt-1 w-56" /></div>
                <div><Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Kategorie</Label><Input value={newTpl.category} onChange={(e) => setNewTpl((f) => ({ ...f, category: e.target.value }))} placeholder="z. B. Reifen" data-testid="workshop-template-category" className="mt-1 w-36" /></div>
                <div><Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Menge</Label><Input type="number" min="1" value={newTpl.quantity} onChange={(e) => setNewTpl((f) => ({ ...f, quantity: e.target.value }))} data-testid="workshop-template-qty" className="mt-1 w-20" /></div>
                <div><Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Preis (CHF)</Label><Input type="number" value={newTpl.unit_price} onChange={(e) => setNewTpl((f) => ({ ...f, unit_price: e.target.value }))} placeholder="0" data-testid="workshop-template-price" className="mt-1 w-28" /></div>
                <div><Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">MwSt %</Label><Input type="number" step="0.1" value={newTpl.tax_rate} onChange={(e) => setNewTpl((f) => ({ ...f, tax_rate: e.target.value }))} placeholder="8.1" data-testid="workshop-template-tax" className="mt-1 w-24" /></div>
                <Button onClick={addTemplate} variant="outline" data-testid="workshop-template-add-btn">{editTplId ? (<><Pencil className="h-4 w-4 mr-1" />Aktualisieren</>) : (<><Plus className="h-4 w-4 mr-1" />Hinzufügen</>)}</Button>
                {editTplId && <Button onClick={cancelEditTemplate} variant="ghost" data-testid="workshop-template-cancel-btn">Abbrechen</Button>}
              </div>
            </CardContent>
          </Card>

          {!planning ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div> : (
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.max(1, planning.bays.length + 1)}, minmax(220px, 1fr))` }}>
              {planning.bays.map((col) => {
                const over = bayOverbooked(col.orders);
                return (
                <Card
                  key={col.bay_id}
                  data-testid={`workshop-plan-bay-${col.bay_id}`}
                  onDragOver={(e) => { if (dragOrderId) e.preventDefault(); }}
                  onDrop={(e) => { e.preventDefault(); if (dragOrderId) assignBay(dragOrderId, col.bay_id); setDragOrderId(null); }}
                  className={`${dragOrderId ? 'ring-2 ring-orange-200' : ''} ${over ? 'border-amber-400 bg-amber-50/40' : ''}`}
                >
                  <CardHeader className="pb-2 flex-row items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-1.5">
                      {col.bay_name}
                      {over && <Badge data-testid={`workshop-bay-overbooked-${col.bay_id}`} className="bg-amber-100 text-amber-800 border-0 text-[10px] gap-1"><AlertTriangle className="h-3 w-3" />Überbucht</Badge>}
                    </CardTitle>
                    <Button variant="ghost" size="sm" data-testid={`workshop-bay-delete-${col.bay_id}`} className="text-red-500 h-7 w-7 p-0" onClick={() => deleteBay(col.bay_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </CardHeader>
                  <CardContent className="space-y-2 min-h-[64px]">
                    {over && <p data-testid={`workshop-bay-overbooked-hint-${col.bay_id}`} className="text-[11px] text-amber-700">Zeitfenster überschneiden sich – bitte einen Termin auf eine andere Bühne ziehen.</p>}
                    {col.orders.length === 0 ? <p className="text-xs text-gray-400">{dragOrderId ? 'Hierher ziehen' : 'Frei'}</p> : col.orders.map((o) => renderPlanCard(o))}
                  </CardContent>
                </Card>
                );
              })}
              {/* Unassigned column */}
              <Card
                data-testid="workshop-plan-unassigned"
                className={`border-dashed ${dragOrderId ? 'ring-2 ring-orange-200' : ''}`}
                onDragOver={(e) => { if (dragOrderId) e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); if (dragOrderId) assignBay(dragOrderId, null); setDragOrderId(null); }}
              >
                <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-500">Nicht zugeteilt</CardTitle></CardHeader>
                <CardContent className="space-y-2 min-h-[64px]">
                  {planning.unassigned.length === 0 ? <p className="text-xs text-gray-400">—</p> : planning.unassigned.map((o) => renderPlanCard(o))}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* REIFENHOTEL */}
        <TabsContent value="reifenhotel" className="mt-4">
          {dueReminders.length > 0 && (
            <Card className="mb-3 border-amber-300 bg-amber-50" data-testid="tire-due-panel">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2 font-semibold text-amber-800">
                  <CalendarClock className="h-4 w-4" />
                  Fällige Saison-Erinnerungen ({dueReminders.length})
                </div>
                <p className="text-xs text-amber-700 mb-3">Der Stichtag ist erreicht. Senden Sie die WhatsApp-Erinnerung mit Reifenwechsel-Buchungslink – oder der Versand läuft morgens automatisch.</p>
                <div className="space-y-2">
                  {dueReminders.map((t) => (
                    <div key={t.id} data-testid={`tire-due-${t.id}`} className="flex items-center justify-between gap-2 rounded-lg bg-white border border-amber-200 px-3 py-2">
                      <div className="text-sm">
                        <span className="font-medium">{t.plate || t.owner_name || '—'}</span>
                        <span className="text-gray-500"> · {t.tire_type} · Stichtag {t.season_reminder_date}</span>
                      </div>
                      <Button size="sm" data-testid={`tire-due-send-${t.id}`} onClick={() => tireReminder(t.id)} className="bg-amber-600 hover:bg-amber-700 shrink-0"><Send className="h-3.5 w-3.5 mr-1" />Senden</Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          <div className="flex justify-end mb-3">
            <Button onClick={openNewTire} data-testid="workshop-new-tire-btn" className="bg-orange-600 hover:bg-orange-700"><Plus className="h-4 w-4 mr-2" />Reifen einlagern</Button>
          </div>
          {tires.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-gray-400" data-testid="tire-empty">Noch keine eingelagerten Reifen.</CardContent></Card>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {tires.map((t) => (
                <Card key={t.id} data-testid={`tire-card-${t.id}`} className="overflow-hidden">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-semibold flex items-center gap-2"><Disc3 className="h-4 w-4 text-orange-600" />{t.tire_type} · {t.quantity} Stk.</div>
                        <div className="text-sm text-gray-600">{t.plate || t.owner_name || '—'}</div>
                      </div>
                      <Badge className={`border-0 ${t.status === 'stored' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{t.status === 'stored' ? 'Eingelagert' : 'Ausgelagert'}</Badge>
                    </div>
                    <div className="text-xs text-gray-500 space-y-0.5">
                      {t.dimension && <div>Dimension: <span className="text-gray-700">{t.dimension}</span></div>}
                      {t.brand && <div>Marke: <span className="text-gray-700">{t.brand}</span></div>}
                      {t.rim && <div>Felge: <span className="text-gray-700">{t.rim}</span></div>}
                      {t.tread_depth && <div>Profil: <span className="text-gray-700">{t.tread_depth}</span></div>}
                      <div>Lagerplatz: <span className="font-medium text-orange-700">{t.location || '—'}</span></div>
                      {t.season_reminder_date && <div>Saison-Erinnerung: <span className="text-gray-700">{t.season_reminder_date}</span></div>}
                      {(t.reminders && t.reminders.length > 0) ? (
                        <div className="pt-1 border-t border-gray-100 mt-1" data-testid={`tire-reminder-log-${t.id}`}>
                          <div className="text-[10px] uppercase tracking-wider text-gray-400">Erinnerungs-Protokoll</div>
                          {t.reminders.slice().reverse().map((r, i) => (
                            <div key={i} className="flex items-center gap-1.5">
                              {r.sent ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : <XCircle className="h-3 w-3 text-red-400" />}
                              <span className={r.sent ? 'text-gray-600' : 'text-red-500'}>
                                {new Date(r.at).toLocaleDateString('de-CH')} · {r.mode === 'auto' ? 'Automatisch' : 'Manuell'}{r.sent ? '' : ' (nicht gesendet)'}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (t.reminded_at && <div className="text-amber-600">Erinnert am {new Date(t.reminded_at).toLocaleDateString('de-CH')}</div>)}
                    </div>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {t.status === 'stored' ? (
                        <Button size="sm" variant="outline" data-testid={`tire-checkout-${t.id}`} onClick={() => tireAction(t.id, 'checkout')}><ArrowRightLeft className="h-3.5 w-3.5 mr-1" />Auslagern</Button>
                      ) : (
                        <Button size="sm" variant="outline" data-testid={`tire-checkin-${t.id}`} onClick={() => tireAction(t.id, 'checkin')}><ArrowRightLeft className="h-3.5 w-3.5 mr-1" />Einlagern</Button>
                      )}
                      <Button size="sm" variant="outline" data-testid={`tire-remind-${t.id}`} onClick={() => tireReminder(t.id)}><Bell className="h-3.5 w-3.5 mr-1" />Saison</Button>
                      <Button size="sm" variant="ghost" data-testid={`tire-edit-${t.id}`} onClick={() => openEditTire(t)}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-500" data-testid={`tire-delete-${t.id}`} onClick={() => deleteTire(t.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* FOTO-FREIGABE */}
        <TabsContent value="freigabe" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button onClick={openNewAppr} data-testid="workshop-new-approval-btn" className="bg-orange-600 hover:bg-orange-700"><Plus className="h-4 w-4 mr-2" />Zusatzarbeiten-Freigabe</Button>
          </div>
          {approvals.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-gray-400" data-testid="approval-empty">Noch keine Freigabe-Anfragen.</CardContent></Card>
          ) : (
            <div className="space-y-3">
              {approvals.map((a) => (
                <Card key={a.id} data-testid={`approval-card-${a.id}`}>
                  <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    {(a.photos || []).length > 0 && (
                      <img src={`${TIRE_FULL}${a.photos[0]}`} alt="" className="w-16 h-16 rounded-lg object-cover border shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold truncate">{a.title}</span>
                        <Badge className={`border-0 ${(APPR_STATUS[a.status] || APPR_STATUS.pending).cls}`} data-testid={`approval-status-${a.id}`}>{(APPR_STATUS[a.status] || APPR_STATUS.pending).label}</Badge>
                      </div>
                      <div className="text-xs text-gray-500">{[(a.plate || a.owner_name), `${(a.photos || []).length} Foto(s)`, `CHF ${Number(a.total || 0).toFixed(2)}`].filter(Boolean).join(' · ')}</div>
                      {a.customer_note && <div className="text-xs text-gray-600 mt-0.5 italic">„{a.customer_note}"</div>}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <Button size="sm" variant="outline" data-testid={`approval-copy-${a.id}`} onClick={() => copyApprLink(a)}><Copy className="h-3.5 w-3.5 mr-1" />Link</Button>
                      <Button size="sm" variant="outline" data-testid={`approval-send-${a.id}`} onClick={() => sendAppr(a.id)}><Send className="h-3.5 w-3.5 mr-1" />WhatsApp</Button>
                      <Button size="sm" variant="ghost" className="text-red-500" data-testid={`approval-delete-${a.id}`} onClick={() => deleteAppr(a.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ORDER DIALOG */}
      <Dialog open={orderDlg} onOpenChange={setOrderDlg}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editOrderId ? 'Auftrag bearbeiten' : 'Neuer Auftrag'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Fahrzeug</Label>
              <Select value={orderForm.vehicle_id || 'none'} onValueChange={(v) => setOrderForm((f) => ({ ...f, vehicle_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="workshop-order-vehicle" className="mt-1"><SelectValue placeholder="Fahrzeug wählen (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Freitext-Kennzeichen —</SelectItem>
                  {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate} · {[v.make, v.model].filter(Boolean).join(' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!orderForm.vehicle_id && (
              <div><Label>Kennzeichen (Freitext)</Label><Input value={orderForm.plate} onChange={(e) => setOrderForm((f) => ({ ...f, plate: e.target.value }))} placeholder="ZH 123456" className="mt-1" /></div>
            )}
            <div><Label>Leistung *</Label><Textarea rows={2} data-testid="workshop-order-desc" value={orderForm.service_description} onChange={(e) => setOrderForm((f) => ({ ...f, service_description: e.target.value }))} placeholder="z. B. Reifenwechsel, Service A" className="mt-1" /></div>
            <div className="rounded-lg border border-gray-200 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Rechnungspositionen</Label>
                <div className="flex items-center gap-2">
                  {templates.length > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button type="button" variant="outline" size="sm" data-testid="workshop-template-menu-btn"><ListPlus className="h-3.5 w-3.5 mr-1" />Vorlage</Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
                        {groupedTemplates.map((g, gi) => (
                          <React.Fragment key={g.category}>
                            {gi > 0 && <DropdownMenuSeparator />}
                            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-gray-400">{g.category}</DropdownMenuLabel>
                            {g.items.map((t) => (
                              <DropdownMenuItem key={t.id} data-testid={`workshop-template-item-${t.id}`} onClick={() => addTemplatePosition(t)}>
                                <span className="flex-1">{(t.quantity > 1 ? `${t.quantity}× ` : '') + t.name}</span>
                                <span className="ml-3 text-gray-400">CHF {Number(t.unit_price).toFixed(0)}</span>
                              </DropdownMenuItem>
                            ))}
                          </React.Fragment>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <Button type="button" variant="outline" size="sm" data-testid="workshop-add-position-btn" onClick={addPosition}><Plus className="h-3.5 w-3.5 mr-1" />Position</Button>
                </div>
              </div>
              {suggestedTemplates.length > 0 && (
                <div className="rounded-md bg-orange-50 border border-orange-200 p-2 space-y-2" data-testid="workshop-suggestions">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-orange-800">Passende Vorlagen zur Leistung – für die Rechnung übernehmen?</span>
                    <Button type="button" size="sm" variant="outline" data-testid="workshop-suggestions-apply-all" onClick={applyAllSuggestions} className="h-7 text-xs border-orange-300 text-orange-700">Alle übernehmen</Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestedTemplates.map((t) => (
                      <button key={t.id} type="button" data-testid={`workshop-suggestion-${t.id}`} onClick={() => addTemplatePosition(t)} className="text-xs rounded-full bg-white border border-orange-300 px-2.5 py-1 text-orange-700 hover:bg-orange-100">
                        + {(t.quantity > 1 ? `${t.quantity}× ` : '') + t.name} · CHF {Number(t.unit_price).toFixed(0)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {(orderForm.positions || []).length === 0 ? (
                <p className="text-xs text-gray-400">Noch keine Positionen – für die spätere Entwurf-Rechnung (bei erledigtem Auftrag).</p>
              ) : (orderForm.positions.map((p, i) => (
                <div key={i} className="flex gap-2 items-center flex-wrap">
                  <Input placeholder="Beschreibung" value={p.description} onChange={(e) => updatePosition(i, 'description', e.target.value)} className="flex-1 min-w-[140px]" data-testid={`workshop-position-desc-${i}`} />
                  <Input type="number" placeholder="Menge" value={p.quantity} onChange={(e) => updatePosition(i, 'quantity', e.target.value)} className="w-16" data-testid={`workshop-position-qty-${i}`} />
                  <Input type="number" placeholder="CHF" value={p.unit_price} onChange={(e) => updatePosition(i, 'unit_price', e.target.value)} className="w-20" data-testid={`workshop-position-price-${i}`} />
                  <select
                    value={p.discount_type || 'none'}
                    onChange={(e) => updatePosition(i, 'discount_type', e.target.value)}
                    data-testid={`workshop-position-disc-type-${i}`}
                    className="h-9 rounded-md border border-slate-300 bg-white px-1 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    title="Rabatt-Art"
                  >
                    <option value="none">—</option>
                    <option value="percent">%</option>
                    <option value="amount">CHF</option>
                  </select>
                  <Input type="number" placeholder="Rabatt" value={p.discount_value ?? ''} disabled={!p.discount_type || p.discount_type === 'none'} onChange={(e) => updatePosition(i, 'discount_value', e.target.value)} className="w-20 disabled:bg-gray-100" data-testid={`workshop-position-disc-value-${i}`} />
                  {p.discount_type && p.discount_type !== 'none' && Number(p.discount_value) > 0 && (
                    <Input placeholder="Rabatt-Grund (z. B. Stammkunde)" value={p.discount_reason ?? ''} onChange={(e) => updatePosition(i, 'discount_reason', e.target.value)} className="w-48" data-testid={`workshop-position-disc-reason-${i}`} />
                  )}
                  <Button type="button" variant="ghost" size="sm" className="text-red-500" onClick={() => removePosition(i)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              )))}
              {invoicePreview.lines.length > 0 && (
                <div className="mt-2 rounded-md bg-gray-50 border border-gray-200 p-2" data-testid="workshop-invoice-preview">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Rechnungsvorschau (inkl. MwSt)</div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-500">
                        <th className="text-left font-medium py-0.5">Position</th>
                        <th className="text-right font-medium py-0.5">Netto</th>
                        <th className="text-right font-medium py-0.5">MwSt</th>
                        <th className="text-right font-medium py-0.5">Brutto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoicePreview.lines.map((l, i) => (
                        <tr key={i} data-testid={`workshop-preview-line-${i}`} className="border-t border-gray-100">
                          <td className="py-0.5 pr-2 truncate max-w-[180px]">{l.qty > 1 ? `${l.qty}× ` : ''}{l.description} <span className="text-gray-400">({l.rate}%)</span>{l.disc > 0 && <span className="text-green-600"> − {l.dtype === 'percent' ? `${l.dval}%` : `CHF ${l.dval}`}</span>}</td>
                          <td className="py-0.5 text-right tabular-nums">{l.net.toFixed(2)}</td>
                          <td className="py-0.5 text-right tabular-nums">{l.tax.toFixed(2)}</td>
                          <td className="py-0.5 text-right tabular-nums">{l.gross.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      {invoicePreview.totals.disc > 0.005 && (
                        <tr className="text-green-600" data-testid="workshop-preview-discount">
                          <td className="py-0.5">Rabatt gesamt (netto)</td>
                          <td colSpan={3} className="py-0.5 text-right tabular-nums">− CHF {invoicePreview.totals.disc.toFixed(2)}</td>
                        </tr>
                      )}
                      <tr className="border-t border-gray-300 font-semibold" data-testid="workshop-preview-totals">
                        <td className="py-1">Total (CHF)</td>
                        <td className="py-1 text-right tabular-nums">{invoicePreview.totals.net.toFixed(2)}</td>
                        <td className="py-1 text-right tabular-nums">{invoicePreview.totals.tax.toFixed(2)}</td>
                        <td className="py-1 text-right tabular-nums">{invoicePreview.totals.gross.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Status</Label>
                <Select value={orderForm.status} onValueChange={(v) => setOrderForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="workshop-order-status" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_KEYS.map((k) => <SelectItem key={k} value={k}>{ORDER_STATUS[k].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Hebebühne</Label>
                <Select value={orderForm.bay_id || 'none'} onValueChange={(v) => setOrderForm((f) => ({ ...f, bay_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="workshop-order-bay" className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— keine —</SelectItem>
                    {bays.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Datum</Label><Input type="date" value={orderForm.scheduled_date} onChange={(e) => setOrderForm((f) => ({ ...f, scheduled_date: e.target.value }))} className="mt-1" /></div>
              <div><Label>Von</Label><Input type="time" value={orderForm.scheduled_start} onChange={(e) => setOrderForm((f) => ({ ...f, scheduled_start: e.target.value }))} className="mt-1" /></div>
              <div><Label>Bis</Label><Input type="time" value={orderForm.scheduled_end} onChange={(e) => setOrderForm((f) => ({ ...f, scheduled_end: e.target.value }))} className="mt-1" /></div>
            </div>
            <div><Label>Zuständig</Label><Input value={orderForm.assigned_to} onChange={(e) => setOrderForm((f) => ({ ...f, assigned_to: e.target.value }))} placeholder="Mechaniker" className="mt-1" /></div>
            <div><Label>Notizen</Label><Textarea rows={2} value={orderForm.notes} onChange={(e) => setOrderForm((f) => ({ ...f, notes: e.target.value }))} className="mt-1" /></div>
          </div>
          <DialogFooter className="flex-wrap gap-2 sm:gap-2">
            {(editOrder?.invoice_id || (editOrder && ['done', 'picked_up'].includes(orderForm.status) && (orderForm.positions || []).some((p) => (p.description || '').trim()))) && (
              <div className="mr-auto flex flex-wrap items-center gap-2">
                <select
                  value={invoiceLang}
                  onChange={(e) => setInvoiceLang(e.target.value)}
                  data-testid="workshop-invoice-lang"
                  title="Rechnungssprache"
                  className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Sprache: auto</option>
                  <option value="de">Deutsch</option>
                  <option value="fr">Français</option>
                  <option value="it">Italiano</option>
                </select>
                {editOrder?.invoice_id ? (
                  <Button variant="outline" onClick={() => downloadInvoicePdf(editOrder.invoice_id, editOrder.invoice_number, invoiceLang || undefined)} data-testid="workshop-dialog-download-invoice-btn">
                    <FileDown className="h-4 w-4 mr-1" />Rechnung-PDF
                  </Button>
                ) : (
                  <Button variant="outline" onClick={createInvoiceFromDialog} data-testid="workshop-dialog-create-invoice-btn">
                    <Receipt className="h-4 w-4 mr-1" />Rechnung erstellen &amp; laden
                  </Button>
                )}
              </div>
            )}
            <Button variant="outline" onClick={() => setOrderDlg(false)}>Abbrechen</Button>
            <Button onClick={saveOrder} data-testid="workshop-save-order-btn" className="bg-orange-600 hover:bg-orange-700">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VEHICLE DIALOG */}
      <Dialog open={vehDlg} onOpenChange={setVehDlg}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editVehId ? 'Fahrzeug bearbeiten' : 'Neues Fahrzeug'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Kennzeichen *</Label><Input data-testid="workshop-vehicle-plate" value={vehForm.plate} onChange={(e) => setVehForm((f) => ({ ...f, plate: e.target.value }))} placeholder="ZH 123456" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Marke</Label><Input value={vehForm.make} onChange={(e) => setVehForm((f) => ({ ...f, make: e.target.value }))} className="mt-1" /></div>
              <div><Label>Modell</Label><Input value={vehForm.model} onChange={(e) => setVehForm((f) => ({ ...f, model: e.target.value }))} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Jahr</Label><Input type="number" value={vehForm.year} onChange={(e) => setVehForm((f) => ({ ...f, year: e.target.value }))} className="mt-1" /></div>
              <div><Label>VIN</Label><Input value={vehForm.vin} onChange={(e) => setVehForm((f) => ({ ...f, vin: e.target.value }))} className="mt-1" /></div>
            </div>
            <div><Label>Halter</Label><Input value={vehForm.owner_name} onChange={(e) => setVehForm((f) => ({ ...f, owner_name: e.target.value }))} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Telefon (Halter)</Label><Input data-testid="workshop-vehicle-phone" value={vehForm.owner_phone} onChange={(e) => setVehForm((f) => ({ ...f, owner_phone: e.target.value }))} placeholder="+41 79 123 45 67" className="mt-1" /></div>
              <div><Label>Nächster Service</Label><Input type="date" data-testid="workshop-vehicle-next-service" value={vehForm.next_service_date} onChange={(e) => setVehForm((f) => ({ ...f, next_service_date: e.target.value }))} className="mt-1" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" data-testid="workshop-vehicle-reminder-optin" checked={vehForm.reminder_opt_in} onChange={(e) => setVehForm((f) => ({ ...f, reminder_opt_in: e.target.checked }))} style={{ accentColor: '#0d9488' }} />
              WhatsApp-Service-Erinnerung an den Halter senden
            </label>
            <div><Label>Notizen</Label><Textarea rows={2} value={vehForm.notes} onChange={(e) => setVehForm((f) => ({ ...f, notes: e.target.value }))} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVehDlg(false)}>Abbrechen</Button>
            <Button onClick={saveVeh} data-testid="workshop-save-vehicle-btn" className="bg-orange-600 hover:bg-orange-700">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AUSWEIS-SCAN */}
      <Dialog open={scanDlg} onOpenChange={setScanDlg}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="workshop-scan-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-orange-600" />Fahrzeugausweis scannen</DialogTitle>
          </DialogHeader>

          {!scanForm && (
            <div
              onDragOver={(e) => { e.preventDefault(); setScanDrag(true); }}
              onDragLeave={() => setScanDrag(false)}
              onDrop={onScanDrop}
              data-testid="workshop-scan-dropzone"
              className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors ${scanDrag ? 'border-orange-500 bg-orange-50' : 'border-gray-300'}`}
            >
              {scanning ? (
                <div className="flex flex-col items-center gap-3 py-6" data-testid="workshop-scan-loading">
                  <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
                  <p className="text-sm text-gray-600">Ausweis wird gelesen…</p>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10 mx-auto text-gray-400 mb-3" />
                  <p className="text-sm text-gray-700 font-medium">Fahrzeugausweis hierher ziehen</p>
                  <p className="text-xs text-gray-500 mt-1 mb-4">Fahrzeug- oder Töffausweis – egal welcher Mobility-Ausweis</p>
                  <div className="flex items-center justify-center gap-2">
                    <label className="cursor-pointer inline-flex items-center gap-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium px-4 py-2">
                      <Upload className="h-4 w-4" />Datei wählen
                      <input type="file" accept="image/*" className="hidden" data-testid="workshop-scan-file-input" onChange={(e) => handleScanFile(e.target.files?.[0])} />
                    </label>
                    <label className="cursor-pointer inline-flex items-center gap-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm font-medium px-4 py-2">
                      <Camera className="h-4 w-4" />Foto aufnehmen
                      <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="workshop-scan-camera-input" onChange={(e) => handleScanFile(e.target.files?.[0])} />
                    </label>
                  </div>
                </>
              )}
            </div>
          )}

          {scanForm && (
            <div className="space-y-4" data-testid="workshop-scan-confirm">
              {scanResult?.extracted?.is_cancelled && (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800" data-testid="workshop-scan-cancelled-warning">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Der Ausweis wirkt <b>abgestempelt/annulliert</b>. Halter und Schildnummer stimmen evtl. nicht mehr – bitte prüfen.</span>
                </div>
              )}
              {scanResult?.existing_vehicle && (
                <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-800" data-testid="workshop-scan-existing-note">
                  Fahrzeug mit diesem Kennzeichen ist bereits erfasst – es wird aktualisiert.
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {scanForm.document_image_url && (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${scanForm.document_image_url}`} alt="Ausweis" className="rounded-lg border w-full object-contain max-h-40 sm:col-span-1" data-testid="workshop-scan-preview" />
                )}
                <div className="sm:col-span-2 space-y-3">
                  <div>
                    <Label className="flex items-center gap-1">Schildnummer *</Label>
                    <Input data-testid="workshop-scan-plate" value={scanForm.plate} onChange={(e) => updateScan('plate', e.target.value)} placeholder="ZH 123456" className="mt-1 font-semibold" />
                    <p className="text-xs text-gray-500 mt-1">Schildnummer korrekt? Bei Bedarf hier anpassen.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Marke</Label><Input value={scanForm.make} onChange={(e) => updateScan('make', e.target.value)} className="mt-1" /></div>
                    <div><Label>Modell</Label><Input value={scanForm.model} onChange={(e) => updateScan('model', e.target.value)} className="mt-1" /></div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div><Label>Art</Label><Input value={scanForm.vehicle_type} onChange={(e) => updateScan('vehicle_type', e.target.value)} className="mt-1" /></div>
                <div><Label>1. Inverkehrs.</Label><Input type="date" value={scanForm.first_registration} onChange={(e) => updateScan('first_registration', e.target.value)} className="mt-1" /></div>
                <div><Label>Farbe</Label><Input value={scanForm.color} onChange={(e) => updateScan('color', e.target.value)} className="mt-1" /></div>
                <div className="col-span-2 sm:col-span-1"><Label>VIN</Label><Input value={scanForm.vin} onChange={(e) => updateScan('vin', e.target.value)} className="mt-1" /></div>
              </div>

              <div className="rounded-lg border p-3 space-y-3">
                <Label className="text-sm font-semibold">Kunde / Halter</Label>
                <div className="flex flex-wrap gap-2" data-testid="workshop-scan-customer-mode">
                  {[
                    { k: 'new', label: 'Wie auf Ausweis anlegen' },
                    { k: 'existing', label: 'Bestehenden Kunden' },
                    { k: 'none', label: 'Kein Kunde' },
                  ].map((m) => (
                    <button key={m.k} type="button" onClick={() => updateScan('customer_mode', m.k)}
                      data-testid={`workshop-scan-mode-${m.k}`}
                      className={`text-sm rounded-full px-3 py-1 border transition-colors ${scanForm.customer_mode === m.k ? 'bg-orange-600 text-white border-orange-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                      {m.label}
                    </button>
                  ))}
                </div>

                {scanForm.customer_mode === 'existing' && (
                  <div>
                    {(scanResult?.customer_matches || []).length > 0 ? (
                      <Select value={scanForm.customer_id || ''} onValueChange={(v) => updateScan('customer_id', v)}>
                        <SelectTrigger data-testid="workshop-scan-customer-select" className="mt-1"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
                        <SelectContent>
                          {(scanResult.customer_matches || []).map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}{c.customer_number ? ` · ${c.customer_number}` : ''}{c.phone ? ` · ${c.phone}` : ''}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-sm text-gray-500">Keine passenden Kunden gefunden. Wähle „Wie auf Ausweis anlegen".</p>
                    )}
                  </div>
                )}

                {scanForm.customer_mode === 'new' && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Nachname *</Label><Input data-testid="workshop-scan-owner-lastname" value={scanForm.owner_last_name} onChange={(e) => updateScan('owner_last_name', e.target.value)} className="mt-1" /></div>
                      <div><Label>Vorname</Label><Input data-testid="workshop-scan-owner-firstname" value={scanForm.owner_first_name} onChange={(e) => updateScan('owner_first_name', e.target.value)} className="mt-1" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Telefon</Label><Input value={scanForm.owner_phone} onChange={(e) => updateScan('owner_phone', e.target.value)} placeholder="+41 79 …" className="mt-1" /></div>
                      <div><Label>Strasse</Label><Input value={scanForm.owner_address} onChange={(e) => updateScan('owner_address', e.target.value)} className="mt-1" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>PLZ</Label><Input value={scanForm.owner_zip} onChange={(e) => updateScan('owner_zip', e.target.value)} className="mt-1" /></div>
                      <div><Label>Ort</Label><Input value={scanForm.owner_city} onChange={(e) => updateScan('owner_city', e.target.value)} className="mt-1" /></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            {scanForm ? (
              <>
                <Button variant="outline" onClick={() => { setScanForm(null); setScanResult(null); }} data-testid="workshop-scan-reset">Neu scannen</Button>
                <Button onClick={confirmScan} data-testid="workshop-scan-confirm-btn" className="bg-orange-600 hover:bg-orange-700">Speichern</Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setScanDlg(false)}>Abbrechen</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REIFENHOTEL DIALOG */}
      <Dialog open={tireDlg} onOpenChange={setTireDlg}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="workshop-tire-dialog">
          <DialogHeader><DialogTitle>{editTireId ? 'Reifensatz bearbeiten' : 'Reifen einlagern'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Fahrzeug (optional)</Label>
              <Select value={tireForm.vehicle_id || 'none'} onValueChange={(v) => (v === 'none' ? setTireForm((f) => ({ ...f, vehicle_id: '' })) : pickVehicleInto(v, setTireForm))}>
                <SelectTrigger data-testid="tire-vehicle" className="mt-1"><SelectValue placeholder="Fahrzeug wählen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Freitext —</SelectItem>
                  {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate}{v.owner_name ? ` · ${v.owner_name}` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kennzeichen</Label><Input data-testid="tire-plate" value={tireForm.plate} onChange={(e) => setTireForm((f) => ({ ...f, plate: e.target.value }))} className="mt-1" /></div>
              <div><Label>Halter</Label><Input value={tireForm.owner_name} onChange={(e) => setTireForm((f) => ({ ...f, owner_name: e.target.value }))} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <Label>Saison</Label>
                <Select value={tireForm.tire_type} onValueChange={(v) => setTireForm((f) => ({ ...f, tire_type: v }))}>
                  <SelectTrigger data-testid="tire-type" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Winter">Winter</SelectItem><SelectItem value="Sommer">Sommer</SelectItem><SelectItem value="Ganzjahr">Ganzjahr</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Anzahl</Label><Input type="number" min="1" data-testid="tire-qty" value={tireForm.quantity} onChange={(e) => setTireForm((f) => ({ ...f, quantity: e.target.value }))} className="mt-1" /></div>
              <div><Label>Dimension</Label><Input data-testid="tire-dimension" value={tireForm.dimension} onChange={(e) => setTireForm((f) => ({ ...f, dimension: e.target.value }))} placeholder="205/55 R16" className="mt-1" /></div>
              <div><Label>Marke</Label><Input value={tireForm.brand} onChange={(e) => setTireForm((f) => ({ ...f, brand: e.target.value }))} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div><Label>Felge</Label><Input value={tireForm.rim} onChange={(e) => setTireForm((f) => ({ ...f, rim: e.target.value }))} placeholder="Alu / Stahl / mit Felgen" className="mt-1" /></div>
              <div><Label>Profiltiefe</Label><Input value={tireForm.tread_depth} onChange={(e) => setTireForm((f) => ({ ...f, tread_depth: e.target.value }))} placeholder="6 mm" className="mt-1" /></div>
              <div><Label>Lagerplatz *</Label><Input data-testid="tire-location" value={tireForm.location} onChange={(e) => setTireForm((f) => ({ ...f, location: e.target.value }))} placeholder="Regal A-12" className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Telefon (für Erinnerung)</Label><Input value={tireForm.owner_phone} onChange={(e) => setTireForm((f) => ({ ...f, owner_phone: e.target.value }))} placeholder="+41 79 …" className="mt-1" /></div>
              <div><Label>Saison-Erinnerung</Label><Input type="date" data-testid="tire-reminder-date" value={tireForm.season_reminder_date || ''} onChange={(e) => setTireForm((f) => ({ ...f, season_reminder_date: e.target.value }))} className="mt-1" /></div>
            </div>
            <div><Label>Notiz</Label><Input value={tireForm.notes} onChange={(e) => setTireForm((f) => ({ ...f, notes: e.target.value }))} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTireDlg(false)}>Abbrechen</Button>
            <Button onClick={saveTire} data-testid="tire-save-btn" className="bg-orange-600 hover:bg-orange-700">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FOTO-FREIGABE DIALOG */}
      <Dialog open={apprDlg} onOpenChange={setApprDlg}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="workshop-approval-dialog">
          <DialogHeader><DialogTitle>Zusatzarbeiten-Freigabe erstellen</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Fahrzeug (optional)</Label>
              <Select value={apprForm.vehicle_id || 'none'} onValueChange={(v) => (v === 'none' ? setApprForm((f) => ({ ...f, vehicle_id: '' })) : pickVehicleInto(v, setApprForm))}>
                <SelectTrigger data-testid="approval-vehicle" className="mt-1"><SelectValue placeholder="Fahrzeug wählen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Freitext —</SelectItem>
                  {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate}{v.owner_name ? ` · ${v.owner_name}` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kennzeichen</Label><Input value={apprForm.plate} onChange={(e) => setApprForm((f) => ({ ...f, plate: e.target.value }))} className="mt-1" /></div>
              <div><Label>Telefon (WhatsApp)</Label><Input value={apprForm.owner_phone} onChange={(e) => setApprForm((f) => ({ ...f, owner_phone: e.target.value }))} placeholder="+41 79 …" className="mt-1" /></div>
            </div>
            <div><Label>Titel *</Label><Input data-testid="approval-title" value={apprForm.title} onChange={(e) => setApprForm((f) => ({ ...f, title: e.target.value }))} placeholder="z. B. Bremsen hinten ersetzen" className="mt-1" /></div>
            <div><Label>Beschreibung</Label><Textarea value={apprForm.description} onChange={(e) => setApprForm((f) => ({ ...f, description: e.target.value }))} placeholder="Was wurde festgestellt?" className="mt-1" /></div>

            <div>
              <Label>Fotos</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                {apprForm.photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={`${TIRE_FULL}${p}`} alt="" className="w-20 h-20 rounded-lg object-cover border" data-testid={`approval-form-photo-${i}`} />
                    <button type="button" onClick={() => apprRemPhoto(i)} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5"><Trash2 className="h-3 w-3" /></button>
                  </div>
                ))}
                <label className="w-20 h-20 rounded-lg border-2 border-dashed flex items-center justify-center cursor-pointer hover:bg-gray-50" data-testid="approval-add-photo">
                  {apprUploading ? <Loader2 className="h-5 w-5 animate-spin text-gray-400" /> : <Camera className="h-5 w-5 text-gray-400" />}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => apprUploadPhoto(e.target.files?.[0])} />
                </label>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Positionen (optional)</Label>
                <Button type="button" size="sm" variant="outline" onClick={apprAddItem} data-testid="approval-add-item"><Plus className="h-3.5 w-3.5 mr-1" />Position</Button>
              </div>
              <div className="space-y-2 mt-1">
                {apprForm.items.map((it, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={it.label} onChange={(e) => apprUpdItem(i, 'label', e.target.value)} placeholder="Bezeichnung" className="flex-1" data-testid={`approval-item-label-${i}`} />
                    <Input type="number" value={it.price} onChange={(e) => apprUpdItem(i, 'price', e.target.value)} placeholder="CHF" className="w-28" data-testid={`approval-item-price-${i}`} />
                    <Button type="button" size="sm" variant="ghost" className="text-red-500" onClick={() => apprRemItem(i)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprDlg(false)}>Abbrechen</Button>
            <Button onClick={saveAppr} disabled={apprSaving} data-testid="approval-save-btn" className="bg-orange-600 hover:bg-orange-700">{apprSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Erstellen'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FAHRZEUG-HISTORIE */}
      {/* QR-Fahrzeugkarte */}
      <Dialog open={qrDlg} onOpenChange={setQrDlg}>
        <DialogContent className="max-w-sm" data-testid="workshop-qr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><QrCode className="h-5 w-5 text-slate-700" />QR-Fahrzeugkarte</DialogTitle>
          </DialogHeader>
          <div className="text-center space-y-3">
            <div className="font-semibold">{qrVeh?.plate || '—'}</div>
            {[qrVeh?.make, qrVeh?.model].filter(Boolean).length > 0 && (
              <div className="text-sm text-gray-500 -mt-2">{[qrVeh?.make, qrVeh?.model].filter(Boolean).join(' ')}</div>
            )}
            <div className="flex justify-center">
              {qrLoading ? <Loader2 className="h-8 w-8 animate-spin text-slate-400 my-10" />
                : qrUrl ? <img src={qrUrl} alt="QR" className="w-56 h-56 rounded-lg border border-gray-200" data-testid="workshop-qr-image" />
                : <div className="py-10 text-gray-400">Kein QR-Code</div>}
            </div>
            <p className="text-xs text-gray-500">Scannen am iPad → öffnet direkt Historie &amp; Ausweis-Scan.</p>
            <Button className="w-full" variant="outline" data-testid="workshop-qr-print-btn" onClick={printQr} disabled={!qrUrl}><FileDown className="h-4 w-4 mr-2" />Drucken</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={histDlg} onOpenChange={setHistDlg}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="workshop-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-orange-600" />Fahrzeug-Historie</DialogTitle>
          </DialogHeader>
          {history && histVehId && (
            <div className="flex flex-wrap gap-2 -mt-1">
              <Button size="sm" variant="outline" data-testid="history-pdf-btn" onClick={() => downloadHistoryPdf(histVehId)}><FileDown className="h-4 w-4 mr-1.5" />Als PDF</Button>
              <Button size="sm" variant="outline" className="border-orange-300 text-orange-700" data-testid="history-scan-btn" onClick={() => { setHistDlg(false); openScan(); }}><ScanLine className="h-4 w-4 mr-1.5" />Ausweis scannen</Button>
            </div>
          )}
          {histLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>
          ) : !history ? (
            <div className="py-10 text-center text-gray-400">Keine Daten.</div>
          ) : (
            <div className="space-y-4">
              {/* Fahrzeug-/Scan-Daten */}
              <Card className="bg-gray-50 border-gray-200" data-testid="history-vehicle-info">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 font-semibold mb-2"><Car className="h-4 w-4 text-orange-600" />{history.vehicle.plate || '—'}{history.vehicle.make ? ` · ${[history.vehicle.make, history.vehicle.model].filter(Boolean).join(' ')}` : ''}</div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                    {history.vehicle.year && <div>Jahr: <span className="text-gray-800">{history.vehicle.year}</span></div>}
                    {history.vehicle.vin && <div>VIN: <span className="text-gray-800">{history.vehicle.vin}</span></div>}
                    {history.vehicle.vehicle_type && <div>Typ: <span className="text-gray-800">{history.vehicle.vehicle_type}</span></div>}
                    {history.vehicle.first_registration && <div>1. Inverkehr: <span className="text-gray-800">{history.vehicle.first_registration}</span></div>}
                    {history.vehicle.color && <div>Farbe: <span className="text-gray-800">{history.vehicle.color}</span></div>}
                    {history.vehicle.owner_name && <div>Halter: <span className="text-gray-800">{history.vehicle.owner_name}</span></div>}
                    {history.vehicle.owner_phone && <div>Telefon: <span className="text-gray-800">{history.vehicle.owner_phone}</span></div>}
                    {(history.vehicle.owner_address || history.vehicle.owner_city) && <div className="col-span-2">Adresse: <span className="text-gray-800">{[history.vehicle.owner_address, [history.vehicle.owner_zip, history.vehicle.owner_city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</span></div>}
                    {history.vehicle.next_service_date && <div>Nächster Service: <span className="text-gray-800">{history.vehicle.next_service_date}</span></div>}
                  </div>
                  <div className="flex gap-2 mt-3 text-xs">
                    <Badge variant="outline" className="border-teal-300 text-teal-700">{history.counts.orders} Aufträge</Badge>
                    <Badge variant="outline" className="border-orange-300 text-orange-700">{history.counts.tires} Reifensätze</Badge>
                    <Badge variant="outline" className="border-purple-300 text-purple-700">{history.counts.approvals} Freigaben</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Zeitachse */}
              {history.events.length === 0 ? (
                <div className="py-8 text-center text-gray-400 text-sm" data-testid="history-empty">Noch keine Ereignisse für dieses Fahrzeug.</div>
              ) : (
                <div className="relative pl-5 space-y-3" data-testid="history-timeline">
                  <div className="absolute left-[7px] top-1 bottom-1 w-px bg-gray-200" />
                  {history.events.map((e, i) => {
                    const meta = e.type === 'order' ? { icon: ClipboardList, dot: 'bg-teal-500', label: 'Auftrag' }
                      : e.type === 'tire' ? { icon: Disc3, dot: 'bg-orange-500', label: 'Reifen' }
                      : { icon: ImageIcon, dot: 'bg-purple-500', label: 'Freigabe' };
                    const Icon = meta.icon;
                    const st = e.status;
                    const stCls = st === 'approved' || st === 'done' || st === 'picked_up' ? 'bg-green-100 text-green-700'
                      : st === 'rejected' ? 'bg-red-100 text-red-700'
                      : st === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600';
                    return (
                      <div key={`${e.type}-${e.id}-${i}`} className="relative" data-testid={`history-event-${i}`}>
                        <span className={`absolute -left-5 top-1 h-3.5 w-3.5 rounded-full border-2 border-white ${meta.dot}`} />
                        <div className="rounded-lg border border-gray-200 p-3">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 text-sm font-medium"><Icon className="h-4 w-4 text-gray-500" />{meta.label}</div>
                            <span className="text-xs text-gray-400">{e.date || '—'}</span>
                          </div>
                          <div className="text-sm text-gray-700 mt-1">{e.title}</div>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            {st && <Badge className={`border-0 text-xs ${stCls}`}>{st}</Badge>}
                            {e.location && <span className="text-xs text-gray-500">Lagerplatz: {e.location}</span>}
                            {typeof e.total === 'number' && e.total > 0 && <span className="text-xs text-gray-500">CHF {Number(e.total).toFixed(2)}</span>}
                            {e.photos ? <span className="text-xs text-gray-500">{e.photos} Foto(s)</span> : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkshopPage;
